import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faThumbsUp,
  faThumbsDown,
} from '@fortawesome/free-solid-svg-icons';
import type { MessageRating, MessageRatingValue } from '../types';

interface RatingWidgetProps {
  /** Previously submitted rating (persisted on the message). */
  rating?: MessageRating;
  /** Rating UI style: 1-5 stars or thumbs up/down. */
  style?: 'stars' | 'thumbs';
  /** Tags offered when the response is rated positively (thumbs up / high stars). */
  positiveTags?: string[];
  /** Tags offered when the response is rated negatively (thumbs down / low stars). */
  negativeTags?: string[];
  /** Submit handler — should reject on failure so the widget can retry. */
  onSubmit: (
    value: MessageRatingValue,
    comment: string,
    tags: string[],
  ) => Promise<void>;
}

const STAR_VALUES: MessageRatingValue[] = [1, 2, 3, 4, 5];

/** Value submitted for a thumbs-up / thumbs-down rating. */
const THUMBS_UP_VALUE: MessageRatingValue = 5;
const THUMBS_DOWN_VALUE: MessageRatingValue = 1;

/**
 * Per-message feedback control. Supports two styles:
 * - "stars": a 1-5 star rating.
 * - "thumbs": thumbs up / down, submitted as 5 (up) or 1 (down).
 * Collapses into a "thanks" state once a rating has been submitted.
 */
const RatingWidget: React.FC<RatingWidgetProps> = ({
  rating,
  style = 'stars',
  positiveTags = [],
  negativeTags = [],
  onSubmit,
}) => {
  const [selected, setSelected] = useState<MessageRatingValue | null>(null);
  const [hovered, setHovered] = useState<MessageRatingValue | null>(null);
  const [comment, setComment] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isThumbs = style === 'thumbs';

  if (rating) {
    if (isThumbs) {
      const isUp = rating.value >= 3;
      return (
        <div className="ves-rating ves-rating--submitted" role="note">
          <span className="ves-rating-thanks">
            <span className="ves-rating-thumbs" aria-hidden="true">
              <FontAwesomeIcon
                icon={isUp ? faThumbsUp : faThumbsDown}
                className="ves-rating-thumb ves-rating-thumb--filled"
              />
            </span>
            Thanks for your feedback
          </span>
          {rating.comment ? (
            <p className="ves-rating-comment-echo">
              &ldquo;{rating.comment}&rdquo;
            </p>
          ) : null}
        </div>
      );
    }

    return (
      <div className="ves-rating ves-rating--submitted" role="note">
        <span className="ves-rating-thanks">
          <span className="ves-rating-stars" aria-hidden="true">
            {STAR_VALUES.map((value) => (
              <FontAwesomeIcon
                key={value}
                icon={faStar}
                className={
                  value <= rating.value
                    ? 'ves-rating-star ves-rating-star--filled'
                    : 'ves-rating-star'
                }
              />
            ))}
          </span>
          Thanks for your feedback
        </span>
        {rating.comment ? (
          <p className="ves-rating-comment-echo">
            &ldquo;{rating.comment}&rdquo;
          </p>
        ) : null}
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!selected || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const tagsToSend = selectedTags.filter((t) => availableTags.includes(t));
      await onSubmit(selected, comment.trim(), tagsToSend);
      // On success the parent replaces this widget with the submitted state.
    } catch {
      setError('Could not send feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const activeValue = hovered ?? selected ?? 0;

  // Positive ratings (thumbs up / high stars) get the positive tag set;
  // negative ratings get the negative set.
  const availableTags =
    selected != null && selected >= 3 ? positiveTags : negativeTags;

  return (
    <div className="ves-rating" onMouseLeave={() => setHovered(null)}>
      <span className="ves-rating-prompt">Rate this response:</span>
      {isThumbs ? (
        <div
          className="ves-rating-thumbs"
          role="radiogroup"
          aria-label="Rate this response with thumbs up or thumbs down"
        >
          <button
            type="button"
            className={`ves-rating-thumb-button ves-rating-thumb-button--up ${
              selected === THUMBS_UP_VALUE
                ? 'ves-rating-thumb-button--active'
                : ''
            }`}
            role="radio"
            aria-checked={selected === THUMBS_UP_VALUE}
            aria-label="Thumbs up"
            onClick={() =>
              setSelected((prev) =>
                prev === THUMBS_UP_VALUE ? null : THUMBS_UP_VALUE,
              )
            }
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faThumbsUp} />
          </button>
          <button
            type="button"
            className={`ves-rating-thumb-button ves-rating-thumb-button--down ${
              selected === THUMBS_DOWN_VALUE
                ? 'ves-rating-thumb-button--active'
                : ''
            }`}
            role="radio"
            aria-checked={selected === THUMBS_DOWN_VALUE}
            aria-label="Thumbs down"
            onClick={() =>
              setSelected((prev) =>
                prev === THUMBS_DOWN_VALUE ? null : THUMBS_DOWN_VALUE,
              )
            }
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faThumbsDown} />
          </button>
        </div>
      ) : (
        <div
          className="ves-rating-stars"
          role="radiogroup"
          aria-label="Rate this response from 1 to 5 stars"
        >
          {STAR_VALUES.map((value) => (
            <button
              key={value}
              type="button"
              className={`ves-rating-star-button ${
                value <= activeValue ? 'ves-rating-star-button--active' : ''
              }`}
              role="radio"
              aria-checked={selected === value}
              aria-label={`${value} star${value === 1 ? '' : 's'}`}
              onMouseEnter={() => setHovered(value)}
              onFocus={() => setHovered(value)}
              onClick={() =>
                setSelected((prev) => (prev === value ? null : value))
              }
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faStar} />
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="ves-rating-details">
          <div className="ves-rating-details-header">
            <span className="ves-rating-details-title">Review Reason</span>
            <span className="ves-rating-details-score">
              {isThumbs
                ? selected === THUMBS_UP_VALUE
                  ? 'Thumbs up'
                  : 'Thumbs down'
                : `${selected} Star${selected === 1 ? '' : 's'}`}
            </span>
          </div>
          {availableTags.length > 0 && (
            <div
              className="ves-rating-tags"
              role="group"
              aria-label="Add tags to your feedback"
            >
              {availableTags.map((tag) => {
                const isActive = selectedTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    className={`ves-rating-tag ${
                      isActive ? 'ves-rating-tag--active' : ''
                    }`}
                    aria-pressed={isActive}
                    onClick={() => toggleTag(tag)}
                    disabled={isSubmitting}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          )}
          <textarea
            className="ves-rating-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="What could be improved? (optional)"
            rows={2}
            disabled={isSubmitting}
          />
          <div className="ves-rating-actions">
            <button
              type="button"
              className="ves-rating-cancel"
              onClick={() => {
                setSelected(null);
                setComment('');
                setSelectedTags([]);
                setError(null);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="ves-rating-submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending…' : 'Submit'}
            </button>
          </div>
          {error && (
            <p className="ves-rating-error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default RatingWidget;
